// Test tab: chat with any guide with this talk-track version pinned (drafts
// included), next to the director's plan after each turn and the exact brief
// the guide saw. Nothing is persisted server-side.

import { useState } from "react";
import { benchTurn, errorText } from "../../services/talkTracks";
import type { BenchTurnResult } from "../../types/talkTrack";
import type { CharacterProfile } from "../../types/guide";

interface Msg {
  kind: "user" | "guide" | "sys";
  text: string;
}

const intro = (version: number): Msg => ({
  kind: "sys",
  text: `Chatting with v${version} pinned. Uses the saved version; nothing is persisted.`,
});

export function TalkTrackBench({
  topicId,
  version,
  dirty,
  guides,
}: {
  topicId: string;
  version: number;
  dirty: boolean;
  guides: CharacterProfile[];
}) {
  const [guideId, setGuideId] = useState("");
  const [messages, setMessages] = useState<Msg[]>([intro(version)]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<BenchTurnResult | null>(null);

  const effectiveGuide = guideId || guides[0]?.id || "";

  function reset(note = "New test session.") {
    setSessionId(null);
    setLast(null);
    setMessages([intro(version), { kind: "sys", text: note }]);
  }

  async function send() {
    const message = input.trim();
    if (!message || busy || !effectiveGuide) return;
    setInput("");
    setMessages((m) => [...m, { kind: "user", text: message }]);
    setBusy(true);
    try {
      const res = await benchTurn({ guideId: effectiveGuide, topicId, version, message, sessionId });
      setSessionId(res.sessionId);
      setLast(res);
      setMessages((m) => [...m, { kind: "guide", text: res.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { kind: "sys", text: `error: ${errorText(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  const plan = last?.plan;
  const facts = plan ? Object.entries(plan.facts) : [];

  return (
    <div className="tt-bench">
      <div className="tt-bench-chat">
        <div className="voice-bar">
          <select aria-label="Guide" value={effectiveGuide} style={{ maxWidth: 220 }}
            onChange={(e) => {
              setGuideId(e.target.value);
              // The bench runtime is bound to one guide — start over.
              if (sessionId) reset("Guide changed — new test session.");
            }}>
            {guides.length === 0 && <option value="">No guides</option>}
            {guides.map((g) => (
              <option key={g.id} value={g.id}>{g.name || g.id}</option>
            ))}
          </select>
          <div className="spacer" />
          <button className="btn btn-sm" onClick={() => reset()}>New session</button>
        </div>
        {dirty && <div className="tt-reason" style={{ padding: "6px 10px" }}>Unsaved edits aren't in the bench — save first.</div>}
        <div className="chat-log">
          {messages.map((m, i) => (
            <div key={i} className={`msg ${m.kind}`}>{m.text}</div>
          ))}
          {busy && <div className="msg sys">…thinking</div>}
        </div>
        <div className="chat-input-row">
          <input
            type="text"
            aria-label="Message"
            placeholder="Say something as a user…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
          />
          <button className="btn btn-primary" onClick={send} disabled={busy || !effectiveGuide}>Send</button>
        </div>
      </div>

      <div className="tt-bench-plan">
        <div className="panel-head">Director plan</div>
        {!plan ? (
          <div className="muted tt-small">Send a message to see the plan the director builds after each turn.</div>
        ) : (
          <>
            <div className="meta-grid">
              <div className="meta-item">
                <span className="meta-label">Stage</span>
                <span className="meta-value">{plan.stage}{plan.furthestStage !== plan.stage ? ` (furthest: ${plan.furthestStage})` : ""}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Topic</span>
                <span className="meta-value">
                  {plan.topicId ? `${plan.topicId} v${plan.version ?? "—"}` : "not matched yet"}
                </span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Last move</span>
                <span className="meta-value">{plan.lastMove ?? "—"}{plan.lastUnitId ? ` · ${plan.lastUnitId}` : ""}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Next move</span>
                <span className="meta-value">{plan.nextMove ?? "—"}{plan.nextUnitId ? ` · ${plan.nextUnitId}` : ""}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Safety signal</span>
                <span className={`meta-value${plan.safetySignal !== "none" ? " tt-weak-text" : ""}`}>{plan.safetySignal}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Ready for offer</span>
                <span className="meta-value">{plan.readyForOffer ? "yes" : "no"}</span>
              </div>
            </div>
            {plan.guidance && (
              <>
                <span className="tt-label">Guidance</span>
                <div className="tt-ro">{plan.guidance}</div>
              </>
            )}
            <span className="tt-label">Facts ({facts.length})</span>
            {facts.length === 0 ? (
              <div className="muted tt-small">None yet.</div>
            ) : (
              <table className="latency-table">
                <tbody>
                  {facts.map(([k, v]) => (
                    <tr key={k}><td className="mono">{k}</td><td>{v}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
            {plan.unitsUsed.length > 0 && (
              <>
                <span className="tt-label">Units used</span>
                <div className="chip-row">
                  {plan.unitsUsed.map((u) => <span key={u} className="chip mono">{u}</span>)}
                </div>
              </>
            )}
            {plan.blockedUnits.length > 0 && (
              <>
                <span className="tt-label">Blocked units</span>
                <div className="chip-row">
                  {plan.blockedUnits.map((u) => <span key={u} className="chip chip-flag mono">{u}</span>)}
                </div>
              </>
            )}
            <span className="tt-label">Brief the guide saw this turn</span>
            <pre className="tt-brief">{last?.briefUsed || "(empty — no plan yet)"}</pre>
            <details className="tt-details">
              <summary>Brief for the next turn</summary>
              <pre className="tt-brief">{last?.nextBrief || "(empty)"}</pre>
            </details>
          </>
        )}
      </div>
    </div>
  );
}
